import React, { useEffect, useState } from "react";
import axios from "axios";
import { Navigate, Link } from "react-router-dom";
import { LogOut, Inbox as InboxIcon, Loader2, Mail, RefreshCw } from "lucide-react";
import Logo from "../../components/site/Logo";
import { useAuth, authHeader } from "../../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (d) =>
  new Date(d).toLocaleString("es-ES", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

export default function Inbox() {
  const { user, logout } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    axios
      .get(`${API}/admin/messages`, { headers: authHeader() })
      .then(({ data }) => setMessages(data))
      .catch(() => setMessages([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (user && user !== false) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (user === null)
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-espana-red" size={32} />
      </div>
    );
  if (user === false) return <Navigate to="/login" replace />;

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/">
            <Logo />
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-sm text-gray-500">{user.email}</span>
            <button
              onClick={logout}
              data-testid="logout-btn"
              className="inline-flex items-center gap-2 border border-gray-300 hover:bg-gray-50 text-sm font-bold px-4 py-2 rounded-sm transition-colors"
            >
              <LogOut size={16} /> Salir
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 lg:px-8 py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-espana-red mb-2">
              Panel privado
            </p>
            <h1 className="text-3xl font-black tracking-tight flex items-center gap-3">
              <InboxIcon className="text-espana-red" size={28} /> Mensajes recibidos
            </h1>
            <p className="mt-2 text-gray-500">
              {messages.length} {messages.length === 1 ? "mensaje" : "mensajes"} en total
            </p>
          </div>
          <button
            onClick={load}
            data-testid="refresh-btn"
            className="inline-flex items-center gap-2 border border-gray-300 hover:bg-white text-sm font-bold px-4 py-2 rounded-sm transition-colors"
          >
            <RefreshCw size={16} /> Actualizar
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="animate-spin text-espana-red" size={32} />
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-20 text-gray-400" data-testid="inbox-empty">
            <Mail size={40} className="mx-auto mb-4" />
            Aún no hay mensajes.
          </div>
        ) : (
          <div className="space-y-4" data-testid="inbox-list">
            {messages.map((m) => (
              <article
                key={m.id}
                data-testid={`message-${m.id}`}
                className="bg-white border border-gray-200 rounded-sm p-6"
              >
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <h3 className="text-lg font-bold">{m.asunto}</h3>
                  <span className="text-xs text-gray-400">{fmt(m.created_at)}</span>
                </div>
                <div className="flex flex-wrap gap-4 text-sm text-gray-500 mb-4">
                  <span className="font-bold text-foreground">{m.nombre}</span>
                  <a href={`mailto:${m.email}`} className="text-espana-red font-medium">
                    {m.email}
                  </a>
                </div>
                <p className="text-gray-700 leading-relaxed whitespace-pre-line border-t border-gray-100 pt-4">
                  {m.mensaje}
                </p>
              </article>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
